import { useCallback, useMemo } from "react";
import { FileUploadType } from "@fixa/platform-shared/dist/fileUpload/fileUpload.type";
import { useWriteSignedUrls } from "@coworker/apprestructured/src/task-management/services/useWriteSignedUrls";
import { useUploadFilesToBucket } from "@coworker/apprestructured/src/task-management/services/useUploadFilesToBucket";
import {
  calcHasSignedUrlsForAllFiles,
  convertToSignedFiles,
  newOrExistingFilesToNewOrExistingUploads,
  newOrExistingUploadsToCreateUploads,
  UploaderError,
  UploaderResult,
} from "@coworker/apprestructured/src/uploader/uploader.helper";
import {
  NewOrExistingFile,
  newOrExistingFilesToFiles,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { NewOrExistingUpload } from "@coworker/apprestructured/src/task-management/types/Upload";

const NO_NEW_OR_EXISTING_UPLOADS: NewOrExistingUpload[] = [];

/**
 * Helper for uploading files to a bucket.
 */
export function useFileUploader(
  newOrExistingFiles: NewOrExistingFile[]
): UploaderResult {
  const newFiles = useMemo(() => {
    return newOrExistingFilesToFiles(newOrExistingFiles);
  }, [newOrExistingFiles]);

  const newFileNames = useMemo(() => {
    return newFiles.map(({ name }) => name);
  }, [newFiles]);

  const { data: signedUrlResponses, error: writeSignedUrlsError } =
    useWriteSignedUrls(newFileNames, FileUploadType.FILE);

  const hasSignedUrlsForAllNewFiles = useMemo(() => {
    return calcHasSignedUrlsForAllFiles(newFiles, signedUrlResponses);
  }, [newFiles, signedUrlResponses]);

  const { mutateAsync: uploadFilesToBucketMutation } = useUploadFilesToBucket();

  const ready = hasSignedUrlsForAllNewFiles;

  const upload = useCallback(async () => {
    if (!ready) {
      throw new Error("File uploader is not ready");
    }
    const signedFiles = convertToSignedFiles(newFiles, signedUrlResponses);
    return uploadFilesToBucketMutation(signedFiles);
  }, [newFiles, ready, uploadFilesToBucketMutation, signedUrlResponses]);

  const newOrExistingUploads = useMemo(() => {
    if (!hasSignedUrlsForAllNewFiles) {
      return NO_NEW_OR_EXISTING_UPLOADS;
    }
    return newOrExistingFilesToNewOrExistingUploads(
      newOrExistingFiles,
      signedUrlResponses
    );
  }, [newOrExistingFiles, hasSignedUrlsForAllNewFiles, signedUrlResponses]);

  const createUploads = useMemo(() => {
    return newOrExistingUploadsToCreateUploads(newOrExistingUploads);
  }, [newOrExistingUploads]);

  const error = useMemo<UploaderError | null>(() => {
    return writeSignedUrlsError ? "FAILED_TO_FETCH_WRITE_SIGNED_URLS" : null;
  }, [writeSignedUrlsError]);

  return { ready, upload, newOrExistingUploads, createUploads, error };
}
